/* System defined core library */
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, Subject } from 'rxjs';

/* User defined library/components */
import { Program } from '../../../shared/models/scheduling/program.model';
import { State } from '../../../shared/models/scheduling/state.model';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { LocationSelector } from '../../../shared/models/scheduling/locationSelector.model';
import { MetricData, MetricDetails } from '../../../shared/models/scheduling/metrics.model';

/* User defined enums/constants */
import { ComponentType, MetricType } from '../../../shared/enums/scheduling.enum';

import { BootstrapParams } from '../../../../bootstrap-params';
import { LocationSelectorComponent } from '../../shared/components/location-selector/location-selector.component';

@Component({
  selector: 'app-find-availability-location',
  templateUrl: './find-availability-location.component.html'
})
export class FindAvailabilityLocationComponent implements OnInit {

  public isBackToBackRequired: boolean = false;
  public isCurrentFormChanged: boolean;
  public region: State;
  public selectedStartDate: any;
  public selectedEndDate: any;
  public selectedIndex: number = 0;
  public selectedAppointments: AppointmentDetail[] = [];
  public isLocationSelected: boolean = false;
  public startDateError: string = '';
  public endDateError: string = '';
  public examName: string;
  public client: Client;
  public exam: Exam;
  public program: Program;
  public metricDetails: MetricDetails;
  public firstTimeSearch: boolean;
  public isGoogleMapValid: boolean = true;
  public selectedCountry: Country;
  public selectedState: State;
  public userCaptchaInput: string = '';

  /* Input-OutPut parameters decalartions */
  @Input() SelectedStartDate: any;
  @Input() SelectedEndDate: any;

  /* Service subscriptions */
  region$: Subscription;
  selectedLocation$: Subscription;
  selectedStartDate$: Subscription;
  selectedEndDate$: Subscription;
  selectedAppointments$: Subscription;
  client$: Subscription;
  program$: Subscription;
  exam$: Subscription;
  metricDetails$: Subscription;
  firstTimeSearch$: Subscription;
  country$: Subscription;
  state$: Subscription;
  translateLanguage$: Subscription;

  @ViewChild(LocationSelectorComponent) locationSelectorInstance: LocationSelectorComponent;

  /* Constructor declaration with required injected services */
  constructor(
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private bootstrapParams: BootstrapParams
  ) {

    this.region$ = selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });

    this.selectedStartDate$ = selectedExamDetailService.selectedStartDate.subscribe(selectedStartDate => {
      this.selectedStartDate = selectedStartDate;

    });

    this.selectedEndDate$ = selectedExamDetailService.selectedEndDate.subscribe(selectedEndDate => {
      this.selectedEndDate = selectedEndDate;
    });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });
    this.program$ = selectedExamDetailService.program.subscribe(program => {
      this.program = program;
    });

    this.exam$ = selectedExamDetailService.exam.subscribe(exam => {
      this.exam = exam;
    });

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    this.metricDetails$ = selectedExamDetailService.metricDetails.subscribe(metricDetails => {
      this.metricDetails = metricDetails;
    });

    this.firstTimeSearch$ = this.selectedExamDetailService.firstTimeSearch.subscribe(firstTimeSearch => {
      this.firstTimeSearch = firstTimeSearch;
    });

    this.country$ = selectedExamDetailService.country.subscribe(country => {
      this.selectedCountry = country;
    });

    this.state$ = selectedExamDetailService.state.subscribe(state => {
      this.selectedState = state;
    });


    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });
    if (this.region != null && this.region != undefined) {
      this.isLocationSelected = true;
    }

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);

    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      }
    );

    this.selectedExamDetailService.getUserCaptchaInput().subscribe(captcha => {
      this.userCaptchaInput = captcha;
    });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    this.isCurrentFormChanged = false;
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.FindAvailabilityLocationComponent)) return;

    if (this.selectedIndex > -1) { this.examName = this.selectedAppointments[this.selectedIndex].exam.name; }

    if (this.selectedAppointments == null || this.selectedAppointments == undefined) {
      this.selectedAppointments = [];
      let tmpAppointmentDetail: AppointmentDetail = new AppointmentDetail();
      tmpAppointmentDetail.client = this.client;
      tmpAppointmentDetail.program = this.program;
      tmpAppointmentDetail.exam = this.exam;
      this.selectedAppointments.push(tmpAppointmentDetail);
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    }
  }

  /* Use to move back to previous screen */
  goBack(): void {
    if (!this.isCurrentFormChanged) {
      this.selectedExamDetailService.setSelectedLocation(null);
    }
    let seatval: LocationSelector = {
      region: this.region, selectedStartDate: this.selectedStartDate, selectedEndDate: this.selectedEndDate
    };
    this.selectedExamDetailService.setSelectedLocation(seatval);
    this.routingService.backwardNavigationSearch(ComponentType.FindAvailabilityLocationComponent);
  }

  /* Use to move next screen */
  submit(event: any): void {
    if(this.bootstrapParams.captchaEnable && this.locationSelectorInstance)
    {
      this.locationSelectorInstance.validateCaptcha();
      this.selectedExamDetailService.getCaptchaValidationResult().subscribe(isCaptchaValid => {
        if (isCaptchaValid) {
          this.proceedToNextPage();
        }
      });
    }
    else{
      this.proceedToNextPage();
    }
  }

  proceedToNextPage(){
    let seatval: LocationSelector = { region: this.region, selectedStartDate: this.selectedStartDate, selectedEndDate: this.selectedEndDate };
    this.selectedExamDetailService.setSelectedLocation(seatval);

    if (this.selectedIndex == 0)
      this.setMetricDetails()

    if (this.selectedIndex > -1) {
      this.selectedAppointments[this.selectedIndex].region = this.region;
      this.selectedAppointments[this.selectedIndex].selectedStartDate = this.selectedStartDate;
      this.selectedAppointments[this.selectedIndex].selectedEndDate = this.selectedEndDate;
      this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
      this.selectedAppointments[this.selectedIndex].hold = new Hold();
    }
    this.selectedExamDetailService.setSlot(null); // Set previous slot NULL.
    this.selectedExamDetailService.setHold(null);// Set previous Hold NULL.
    this.routingService.searchFwdNavigation(ComponentType.FindAvailabilityLocationComponent);
  }

  public isDisable(): boolean {
    return (
      !this.isLocationSelected
      || this.selectedStartDate == null
      || (this.endDateError != '')
      || (this.startDateError != '')
      || (this.userCaptchaInput == '' && this.bootstrapParams.captchaEnable)
    );
  }

  public getErrorMessage(event: any): void {
    this.endDateError = event.endDateError;
    this.startDateError = event.startDateError;
  }

  public getSelectedLocation(event: any): void {
    this.isLocationSelected = event.isLocationSelected;
    this.isCurrentFormChanged = event.isCurrentFormChanged
  }

  /* Validate Region selector is valid or not */
  validateRegionSelector() {
    if (this.selectedCountry == null || this.selectedCountry == undefined) {
      this.isLocationSelected = false;
    }
    else if ((this.selectedCountry != null || this.selectedCountry != undefined) &&
      (this.selectedState != null && this.selectedState != undefined && this.selectedState.id == "-1")) {
      this.isLocationSelected = true;
    }
    else if ((this.selectedCountry != null || this.selectedCountry != undefined) &&
      (this.selectedState != null && this.selectedState != undefined && this.selectedState.id != "-1")) {
      this.isLocationSelected = true;
    }
    else {
      this.isLocationSelected = false;
    }
  }

  validRegionSelector(event: any) {
    this.isLocationSelected = event;
  }

  /* Set MetricDetails in shared service */
  setMetricDetails(): void {

    if (this.region) {
      let searched =
      new MetricData(MetricType.Searched,
        { "latitude": this.region.lat, "longitude": this.region.long },
        this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-")
      );
      this.metricDetails = new MetricDetails(searched);
      if (!this.firstTimeSearch) {
        this.selectedExamDetailService.setFirstTimeSearch(true);
        this.selectedExamDetailService.setMetricDetails(this.metricDetails);
      } else {
        this.selectedExamDetailService.setFirstTimeSearch(false);
      }
    }
  }
}
